/**
 * Files Page - Document Management
 *
 * Top-level page for managing company documents with:
 * - Folder-based organization (sidebar)
 * - Two document types: uploaded files and Google Doc links
 * - Search and filter
 * - Upload with drag-and-drop
 * - Version history for uploaded files
 */
import { useState, useRef } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Upload, Link2, Search, FolderPlus, Plus, Download, Trash2,
  FileText, Image, File as FileIcon, ExternalLink, MoreHorizontal,
  Clock, FolderOpen, ChevronRight, History, Loader2, Pencil, X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  useDocumentFolders,
  useDocuments,
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
  useCreateLinkDocument,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
  useDocument,
  useUploadVersion,
} from '@/hooks/useDocuments';
import { api } from '@/lib/api';
import type { Document, DocumentFolder, DocumentVersion } from '@/hooks/useDocuments';

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(contentType: string | null) {
  if (!contentType) return FileIcon;
  if (contentType.startsWith('image/')) return Image;
  if (contentType === 'application/pdf') return FileText;
  return FileIcon;
}

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffDays > 30) return then.toLocaleDateString();
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  return 'just now';
}

// ============================================================================
// Main Page
// ============================================================================

export function FilesPage() {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  const { data: foldersData, isLoading: foldersLoading } = useDocumentFolders();
  const { data: documents, isLoading: docsLoading } = useDocuments({
    folder_id: selectedFolder || undefined,
    search: searchQuery || undefined,
  });

  const folders = foldersData?.folders || [];
  const unfiledCount = foldersData?.unfiled_count || 0;

  // Total doc count from folder metadata (not affected by current filter)
  const totalDocs = folders.reduce((sum, f) => sum + (f.document_count || 0), 0) + unfiledCount;

  return (
    <DashboardLayout
      title="Files"
      actions={
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowUploadDialog(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload File
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowLinkDialog(true)}>
                <Link2 className="mr-2 h-4 w-4" />
                Add Link (Google Doc, etc.)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowFolderDialog(true)}>
                <FolderPlus className="mr-2 h-4 w-4" />
                New Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="flex gap-6 h-full">
        {/* Folder Sidebar */}
        <FolderSidebar
          folders={folders}
          unfiledCount={unfiledCount}
          totalDocs={totalDocs}
          selectedFolder={selectedFolder}
          foldersLoading={foldersLoading}
          onSelectFolder={setSelectedFolder}
        />

        {/* Document List */}
        <div className="flex-1 min-w-0">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Document Table */}
          {docsLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading documents...
            </div>
          ) : !documents || documents.length === 0 ? (
            <EmptyState
              hasSearch={!!searchQuery}
              hasFolder={!!selectedFolder}
              onUpload={() => setShowUploadDialog(true)}
              onAddLink={() => setShowLinkDialog(true)}
            />
          ) : (
            <DocumentTable
              documents={documents}
              onViewDetail={(id) => { setSelectedDocId(id); setShowDetailDialog(true); }}
            />
          )}
        </div>
      </div>

      {/* Dialogs */}
      <UploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        folders={folders}
        defaultFolderId={selectedFolder && selectedFolder !== 'unfiled' ? selectedFolder : undefined}
      />
      <LinkDialog
        open={showLinkDialog}
        onOpenChange={setShowLinkDialog}
        folders={folders}
        defaultFolderId={selectedFolder && selectedFolder !== 'unfiled' ? selectedFolder : undefined}
      />
      <FolderDialog
        open={showFolderDialog}
        onOpenChange={setShowFolderDialog}
      />
      <DocumentDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        documentId={selectedDocId}
      />
    </DashboardLayout>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState({
  hasSearch, hasFolder, onUpload, onAddLink,
}: {
  hasSearch: boolean;
  hasFolder: boolean;
  onUpload: () => void;
  onAddLink: () => void;
}) {
  if (hasSearch) {
    return (
      <div className="text-center py-12">
        <Search className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="text-sm font-medium mb-1">No results found</h3>
        <p className="text-sm text-muted-foreground">Try a different search term</p>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <FolderOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
      <h3 className="text-sm font-medium mb-1">
        {hasFolder ? 'No documents in this folder' : 'No documents yet'}
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Upload files or add links to Google Docs for easy access
      </p>
      <div className="flex justify-center gap-2">
        <Button size="sm" onClick={onUpload}>
          <Upload className="h-4 w-4 mr-1" /> Upload File
        </Button>
        <Button size="sm" variant="outline" onClick={onAddLink}>
          <Link2 className="h-4 w-4 mr-1" /> Add Link
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Folder Sidebar
// ============================================================================

function FolderSidebar({
  folders, unfiledCount, totalDocs, selectedFolder, foldersLoading, onSelectFolder,
}: {
  folders: DocumentFolder[];
  unfiledCount: number;
  totalDocs: number;
  selectedFolder: string | null;
  foldersLoading: boolean;
  onSelectFolder: (id: string | null) => void;
}) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  const startRename = (folder: DocumentFolder) => {
    setRenamingId(folder.id);
    setRenameValue(folder.name);
  };

  const saveRename = async (folderId: string) => {
    if (renameValue.trim()) {
      await updateFolder.mutateAsync({ id: folderId, name: renameValue.trim() });
    }
    setRenamingId(null);
  };

  return (
    <div className="w-56 shrink-0">
      <div className="space-y-1">
        <button
          onClick={() => onSelectFolder(null)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            selectedFolder === null
              ? 'bg-primary/10 text-primary'
              : 'hover:bg-muted text-foreground'
          }`}
        >
          <FolderOpen className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">All Files</span>
          <span className="text-xs text-muted-foreground">{totalDocs}</span>
        </button>

        {foldersLoading ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">Loading...</div>
        ) : (
          folders.map((folder) => (
            <div key={folder.id} className="relative group">
              {renamingId === folder.id ? (
                <div className="px-3 py-1.5">
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename(folder.id);
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    onBlur={() => saveRename(folder.id)}
                    autoFocus
                    className="h-7 text-sm"
                  />
                </div>
              ) : (
                <div className="flex items-center">
                  <button
                    onClick={() => onSelectFolder(folder.id)}
                    className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedFolder === folder.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <FolderOpen className="h-4 w-4 shrink-0" />
                    <span className="flex-1 text-left truncate">{folder.name}</span>
                    <span className="text-xs text-muted-foreground group-hover:hidden">{folder.document_count}</span>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="hidden group-hover:inline-flex h-7 w-7 items-center justify-center rounded hover:bg-muted-foreground/10 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={4}>
                      <DropdownMenuItem onClick={() => startRename(folder)}>
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(`Delete folder "${folder.name}"? Documents inside will become unfiled.`)) {
                            deleteFolder.mutate(folder.id);
                            if (selectedFolder === folder.id) onSelectFolder(null);
                          }
                        }}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          ))
        )}

        <button
          onClick={() => onSelectFolder('unfiled')}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
            selectedFolder === 'unfiled'
              ? 'bg-primary/10 text-primary font-medium'
              : 'hover:bg-muted text-muted-foreground'
          }`}
        >
          <FileIcon className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">Unfiled</span>
          <span className="text-xs text-muted-foreground">{unfiledCount}</span>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Document Table
// ============================================================================

function DocumentTable({
  documents,
  onViewDetail,
}: {
  documents: Document[];
  onViewDetail: (id: string) => void;
}) {
  const deleteDoc = useDeleteDocument();

  const handleDownload = async (doc: Document) => {
    if (doc.document_type === 'link' && doc.external_url) {
      window.open(doc.external_url, '_blank');
    } else if (doc.document_type === 'file' && doc.file_name) {
      await api.documents.download(doc.id, doc.file_name);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Name</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground w-28">Folder</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground w-20">Type</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground w-20">Size</th>
            <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground w-28">Updated</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => {
            const Icon = doc.document_type === 'link' ? Link2 : getFileIcon(doc.content_type);
            return (
              <tr key={doc.id} className="border-b last:border-0 hover:bg-muted/30 group">
                <td className="px-4 py-2.5">
                  <button
                    className="flex items-center gap-2 min-w-0 text-left"
                    onClick={() => handleDownload(doc)}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{doc.title}</div>
                      {doc.description && (
                        <div className="text-xs text-muted-foreground truncate">{doc.description}</div>
                      )}
                    </div>
                    {doc.document_type === 'link' && (
                      <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                    )}
                  </button>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {doc.folder_name || '—'}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    doc.document_type === 'link'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {doc.document_type === 'link' ? 'Link' : (doc.content_type?.split('/')[1]?.toUpperCase() || 'File')}
                  </span>
                  {doc.document_type === 'file' && doc.current_version > 1 && (
                    <span className="ml-1 text-[10px] text-muted-foreground">v{doc.current_version}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {doc.document_type === 'file' ? formatFileSize(doc.file_size) : '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {timeAgo(doc.updated_at)}
                </td>
                <td className="px-4 py-2.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {doc.document_type === 'file' && (
                        <DropdownMenuItem onClick={() => handleDownload(doc)}>
                          <Download className="mr-2 h-4 w-4" /> Download
                        </DropdownMenuItem>
                      )}
                      {doc.document_type === 'link' && (
                        <DropdownMenuItem onClick={() => handleDownload(doc)}>
                          <ExternalLink className="mr-2 h-4 w-4" /> Open Link
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => onViewDetail(doc.id)}>
                        <Pencil className="mr-2 h-4 w-4" /> Details / Edit
                      </DropdownMenuItem>
                      {doc.document_type === 'file' && (
                        <DropdownMenuItem onClick={() => onViewDetail(doc.id)}>
                          <History className="mr-2 h-4 w-4" /> Version History
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(`Delete "${doc.title}"? This cannot be undone.`)) {
                            deleteDoc.mutate(doc.id);
                          }
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Upload Dialog
// ============================================================================

function UploadDialog({
  open, onOpenChange, folders, defaultFolderId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: DocumentFolder[];
  defaultFolderId?: string;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState(defaultFolderId || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadDoc = useUploadDocument();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    await uploadDoc.mutateAsync({
      file: selectedFile,
      title: title || selectedFile.name,
      description: description || undefined,
      folderId: folderId || undefined,
    });

    setTitle('');
    setDescription('');
    setSelectedFile(null);
    setFolderId(defaultFolderId || '');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload File</DialogTitle>
          <DialogDescription>Upload a document to your company files.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>File</Label>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFile(file);
                  if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
                }
              }}
            />
            <div
              className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-xs text-muted-foreground">({formatFileSize(selectedFile.size)})</span>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Click to select a file (max 50MB)</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="upload-title">Title</Label>
            <Input
              id="upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              required
            />
          </div>
          <div>
            <Label htmlFor="upload-desc">Description (optional)</Label>
            <Input
              id="upload-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>
          <div>
            <Label htmlFor="upload-folder">Folder</Label>
            <select
              id="upload-folder"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">No folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!selectedFile || uploadDoc.isPending}>
              {uploadDoc.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-4 w-4 mr-1" /> Upload</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Link Dialog
// ============================================================================

function LinkDialog({
  open, onOpenChange, folders, defaultFolderId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: DocumentFolder[];
  defaultFolderId?: string;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState(defaultFolderId || '');
  const createLink = useCreateLinkDocument();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createLink.mutateAsync({
      title,
      external_url: url,
      description: description || undefined,
      folder_id: folderId || undefined,
    });
    setTitle('');
    setUrl('');
    setDescription('');
    setFolderId(defaultFolderId || '');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Link</DialogTitle>
          <DialogDescription>Link to a Google Doc, Sheet, or any URL.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="link-title">Title</Label>
            <Input
              id="link-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Project Kickoff SOP"
              required
            />
          </div>
          <div>
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/document/d/..."
              type="url"
              required
            />
          </div>
          <div>
            <Label htmlFor="link-desc">Description (optional)</Label>
            <Input
              id="link-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>
          <div>
            <Label htmlFor="link-folder">Folder</Label>
            <select
              id="link-folder"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">No folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createLink.isPending}>
              {createLink.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Adding...</>
              ) : (
                <><Link2 className="h-4 w-4 mr-1" /> Add Link</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Folder Dialog
// ============================================================================

function FolderDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createFolder = useCreateFolder();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createFolder.mutateAsync({
      name,
      description: description || undefined,
    });
    setName('');
    setDescription('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
          <DialogDescription>Create a folder to organize your documents.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="folder-name">Name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., HR Documents"
              required
            />
          </div>
          <div>
            <Label htmlFor="folder-desc">Description (optional)</Label>
            <Input
              id="folder-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What goes in this folder?"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createFolder.isPending}>
              {createFolder.isPending ? 'Creating...' : 'Create Folder'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Document Detail Dialog (Edit + Version History)
// ============================================================================

function DocumentDetailDialog({
  open, onOpenChange, documentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | null;
}) {
  const { data, isLoading } = useDocument(documentId);
  const updateDoc = useUpdateDocument();
  const uploadVersion = useUploadVersion();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [changeNote, setChangeNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const doc = data?.document;
  const versions = data?.versions || [];

  const startEdit = () => {
    if (doc) {
      setEditTitle(doc.title);
      setEditDescription(doc.description || '');
      setEditing(true);
    }
  };

  const saveEdit = async () => {
    if (!doc) return;
    await updateDoc.mutateAsync({
      id: doc.id,
      title: editTitle,
      description: editDescription || undefined,
    });
    setEditing(false);
  };

  const handleVersionUpload = async (file: File) => {
    if (!documentId) return;
    await uploadVersion.mutateAsync({
      documentId,
      file,
      changeNote: changeNote || undefined,
    });
    setChangeNote('');
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Document Details</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
          </div>
        ) : doc ? (
          <div className="space-y-4">
            {/* Metadata */}
            {editing ? (
              <div className="space-y-3">
                <div>
                  <Label>Title</Label>
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdit} disabled={updateDoc.isPending}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{doc.title}</h3>
                    {doc.description && (
                      <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={startEdit}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>Type: {doc.document_type === 'link' ? 'Link' : 'File'}</span>
                  {doc.folder_name && <span>Folder: {doc.folder_name}</span>}
                  {doc.file_name && <span>File: {doc.file_name}</span>}
                  {doc.file_size && <span>Size: {formatFileSize(doc.file_size)}</span>}
                  <span>Created: {new Date(doc.created_at).toLocaleDateString()}</span>
                  {doc.created_by && <span>By: {doc.created_by}</span>}
                </div>
                {doc.document_type === 'link' && doc.external_url && (
                  <a
                    href={doc.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" /> Open Link
                  </a>
                )}
              </div>
            )}

            {/* Version History (file documents only) */}
            {doc.document_type === 'file' && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <History className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">
                    Version History ({versions.length} version{versions.length !== 1 ? 's' : ''})
                  </h4>
                </div>

                {/* Upload new version */}
                <div className="mb-3 p-3 border border-dashed rounded-lg">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await handleVersionUpload(file);
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="What changed? (optional)"
                      value={changeNote}
                      onChange={(e) => setChangeNote(e.target.value)}
                      className="flex-1 h-8 text-xs"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadVersion.isPending}
                    >
                      {uploadVersion.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Upload className="h-3 w-3 mr-1" /> New Version</>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Version list */}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-muted/50 group"
                    >
                      <span className="font-medium w-8 shrink-0">v{v.version_number}</span>
                      <span className="truncate flex-1">
                        {v.file_name}
                        {v.change_note && (
                          <span className="ml-1 text-muted-foreground">— {v.change_note}</span>
                        )}
                      </span>
                      <span className="text-muted-foreground shrink-0">{formatFileSize(v.file_size)}</span>
                      {v.uploaded_by && (
                        <span className="text-muted-foreground shrink-0 hidden sm:inline">{v.uploaded_by}</span>
                      )}
                      <span className="text-muted-foreground shrink-0">{timeAgo(v.created_at)}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 opacity-0 group-hover:opacity-100"
                        onClick={() => api.documents.downloadVersion(v.id, v.file_name)}
                        title="Download this version"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">Document not found.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
